import type { Language } from '@seek/ca-myprofile-utils';
import type { Dispatch } from 'redux';
import { isGraphQLAuthError } from 'src/modules/graphql/errors';
import type { PageUpdateActionType } from '../page/actionCreators';

import {
  type Country as ZoneCountry,
  zoneFromBrandAndCountry,
} from '@seek/audience-zones';
import type { Brand, Country } from '@seek/melways-sites';
import analytics from 'src/modules/analytics';

export enum UserActionType {
  SUCCESS = 'USER_SUCCESS',
  ERROR = 'USER_ERROR',
  UPDATE_USER_STATE = 'UPDATE_USER_STATE',
  USER_DELETED = 'USER_DELETED',
  SET_USER_DETAILS = 'SET_USER_DETAILS',
}

export interface UserAction {
  type: UserActionType | PageUpdateActionType;
  payload?: any;
}

export interface ChangeEmailModel {
  confirmEmail: string;
  currentPassword: string;
  newEmail: string;
  error?: string;
}

export interface ChangePasswordModel {
  confirmPassword: string;
  currentPassword: string;
  newPassword: string;
}

export interface DeleteAccountModel {
  password: string;
  language?: Language;
}
export type GetUser = (
  language: Language,
  country: Country,
  brand: Brand,
) => (arg: { dispatch: Dispatch<UserAction>; api: any }) => void;

export const getUser: GetUser =
  (language: Language = 'en', country: Country, brand: Brand) =>
  async ({ dispatch, api }) => {
    try {
      const {
        data: { viewer: data },
      } = await api.getUser();
      dispatch({
        type: UserActionType.SUCCESS,
        payload: data,
      });
      return data;
    } catch (error: any) {
      const userNotAuthenticated: boolean = isGraphQLAuthError(error);
      // send a fail event and then dispatch action that redirects
      const countryForAudienceZones = country?.toUpperCase() as ZoneCountry;
      const zone =
        zoneFromBrandAndCountry(brand as Brand, countryForAudienceZones) || '';
      const { trackLink } = analytics({});
      trackLink({
        eventName: 'settings_displayed',
        currentPage: 'account settings',
        loginId: error?.id,
        isLoggedIn: false,
        loginGuid: error?.emailCorrelationId,
        error,
        brand,
        country,
        zone,
      });
      dispatch({
        type: UserActionType.ERROR,
        payload: {
          language,
          userNotAuthenticated,
        },
      });
    }
  };

export const updateUserInfo =
  (
    loginId?: string,
    isLoggedIn?: boolean,
    loginGuid?: string,
    userLanguage?: string,
  ) =>
  ({ dispatch }: { dispatch: Dispatch<UserAction>; getState: any }) => {
    dispatch({
      type: UserActionType.SET_USER_DETAILS,
      payload: {
        loginId,
        isLoggedIn,
        loginGuid,
        userLanguage,
      },
    });
  };
